import { General } from "../../../../siteManagement/general.js";
import { Sheet, SheetEventType } from "../../../../siteManagement/sheet.js";
import { InGridButtonEnabledParams } from "../../../address/openingHoursInGrid.js";
import { ResizeDetector } from "../../../statistics/resize_detector.js";
import { Config } from "../../config/config.js";
import { ColumnInfo, ComponentHelper } from "../../util/componentHelper.js";
import { DialogHelper } from "../../util/dialogHelper.js";
import { Helper } from "../../util/helper.js";
import { IngridHelper } from "../../util/ingridHelper.js";
import { NotificationHelper } from "../../util/notificationHelper.js";
import { Entity, Metadata } from "../commonApp.js";
import { IsSearchField, IsSearchFieldProps } from "../isSearchField.js";
import { ToolBar, ToolBarCustomButton, ToolBarInGridButtonType, ToolBarStandardIconClass } from "../toolBar.js";
import { EditableGridHelper } from "./editableGridHelper.js";
import { EditInGridRow, EditInGridRowProps, getEditInGridMetaData } from "./editInGridRow.js";
import GridDetails from "./gridDetails.js";
import { ValidationBuilder } from "./validationBuilder.js";
import { InnosoftMobileApp } from "../../util/innosoftMobileApp.js";
import { fileIcons } from "../../../documents/documents.js";
import ToolBarContextMenu, { ToolBarContextMenuOpenParams } from "../mobile/toolBarContextMenu.js";
import React from "react";
import ReactDOM from "react-dom";
const additonalDataSuffix = "_additional_data";

export interface EditableGridProps {
    id: string;
    gridOptions?: any;
    columns?: BaseColumn[],
    schema?: Schema,
    editable?: boolean,
    selectable?: boolean | string;
    showSelectableCheckBox?: boolean;
    selectableCheckBoxHeaderTemplate?: string;
    validatable?: boolean,
    groupable?: boolean;
    sortable?: boolean
    filterable?: boolean
    pageable?: boolean
    scrollable?: boolean
    data: any,
    resolveAdditionalData?: any,
    onRequestItemValidation?: (columns: BaseColumn[], dataItem: any) => ValidationResult,
    onDataChanged?: (action: "itemchange" | "add" | "remove", fieldName: string, dataItems: any[]) => void;
    onFieldChanged?: (e: any) => void;
    onEdit?: (e: any) => void;
    onEditEnd?: (e: kendo.ui.GridCellCloseEvent) => void;
    onSelectionChange?: (e: kendo.ui.GridChangeEvent) => void;
    filters?: kendo.data.DataSourceFilterItem;
    onRowClicked?: (e, dataItem) => void;
    onSaveSuccesfully?: (data) => void;
    rowReadOnly?: (dataItem: any) => boolean;
    validateAll?: boolean;
    buttons?: Array<InGridToolBarButton>;
    onButtonClick?: (params: InGridButtonClickParams) => void;
    gridDetails?: {
        useFeature: boolean;
    };
    useFloatingButtons?: boolean;
    onNavigate?: (e: kendo.ui.GridNavigateEvent) => void;
    onDataBound?: (e: kendo.ui.GridDataBoundEvent) => void;
    onFilter?: (e: kendo.ui.GridFilterEvent) => void;
    onLayoutReady?: (columns: BaseColumn[], schema: Schema) => void;
    helpEditUrl?: string;
}
export class EditableGrid extends React.Component<EditableGridProps, {
    Schema: Schema;
    Columns: BaseColumn[];
}> {
    private gridRef: HTMLDivElement;
    private grid: kendo.ui.Grid = null;
    private gridId = kendo.guid();
    private validationRenderer = new KendoValidationMessageRenderer();
    private fetchingCallbacks: any = {};
    private kendoColumns: any[];
    private editableSheet: Sheet;
    private contextMenu: ToolBarContextMenu;

    constructor(props) {
        super(props);
        this.state = {
            Columns: this.props.columns,
            Schema: this.props.schema
        }
    }

    public getGrid(): kendo.ui.Grid {
        return this.grid;
    }

    public getGridData = (): GridData => {
        return {
            Data: (this.dataSource.data() as any),
            Deleted: this.dataSource["destroyed"]()
        };
    }

    public getData = (): kendo.data.ObservableArray => {
        return this.dataSource?.data();
    }

    public getDataSource = (): kendo.data.DataSource => {
        return this.dataSource;
    }

    public refreshColumnTemplate = (changedDataItem: any, columnName: string) => {
        let value = changedDataItem.get(columnName);
        changedDataItem.set(columnName, null);
        changedDataItem.set(columnName, value);
    }

    public getSelectedItems = () => {
        const rows = this.grid.select();
        const items = rows.map((index, row) => { return this.grid.dataItem(row) });
        return items;
    }

    private submitChanges = (e) => {
        const data = e.data;
        e.success(data.updated, "update");
        e.success(data.created, "create");
        e.success(data.destroyed, "destroy");
    }

    private handleDatasourceChanged = (e: kendo.data.DataSourceChangeEvent) => {
        if (e.action && (e.action == "itemchange" || e.action == "add" || e.action == "remove")) {
            this.props.onDataChanged && this.props.onDataChanged(e.action, e.field, e.items);
            if (e.items.length > 0) {
                let dataItem: any = e.items[0];
                this.mapDataToIndFields(dataItem);
                if (e.action == "add") {
                    this.setDefaultValues(this.state.Columns, dataItem);
                }
            }
            if (this.editableSheet) {
                if (e.items[0]["preventOnDataChanged"])
                    delete e.items[0]["preventOnDataChanged"];
                else
                    this.editableSheet.trigger(SheetEventType.Custom, { command: "onDataChanged" });
            }
        }
    }

    private createDataSource = (schema: Schema) => {
        const kendoFields = {};

        for (let f in schema.fields) {
            kendoFields[f] = {
                type: schema.fields[f].Type,
                nullable: true,
                editable: schema.fields[f].editable,
                validation: schema.fields[f].Validation
            }
        }

        return new kendo.data.DataSource({
            transport: {
                read: (e) => { e.success(this.props.data) },
                submit: this.submitChanges
            },
            batch: true,
            pageSize: (this.props.pageable ?? true) ? 100 : undefined,
            change: this.handleDatasourceChanged,
            schema: {
                model: { id: schema.Id, fields: kendoFields }
            },
            group: schema.group,
            aggregate: schema.aggregate,
            filter: this.props.filters ?? this.props.filters
        });
    }

    private getSearchParamsByType = (searchColumn: SearchBaseColumn, name, options): IsSearchFieldProps => {
        let searchWindowUrl = EditableGridHelper.helper.evalRow(searchColumn.searchWindowUrl, options.model);
        const componentHelper = ComponentHelper.createFactory();
        const componentSearchProps: IsSearchFieldProps = componentHelper.getDefaultSearchFieldProps();
        componentSearchProps.field.name = null;
        componentSearchProps.field.model = name;
        componentSearchProps.multiSelectMode = false;
        componentSearchProps.field.searchWindowUrl = searchWindowUrl;
        componentSearchProps.field.dataTextField = searchColumn.dataTextField;
        componentSearchProps.field.dataValueField = searchColumn.dataValueField;
        componentSearchProps.field.searchValueField = searchColumn.searchValueField;
        componentSearchProps.field.searchTextField = searchColumn.searchTextField;
        componentSearchProps.field.autocompleteEnabled = searchColumn.autocompleteEnabled;
        componentSearchProps.field.autocompleteDataTextField = searchColumn.autocompleteDataTextField;
        componentSearchProps.field.autocompleteUrl = searchColumn.autocompleteUrl;
        componentSearchProps.field.autocompleteTemplate = searchColumn.autocompleteTemplate;
        componentSearchProps.field.readOnlyTextField = searchColumn.readOnlyTextField;
        componentSearchProps.field.fullObjectKey = searchColumn.fullObjectKey;
        componentSearchProps.field.deleteButtonEnabled = searchColumn.deleteButtonEnabled;
        componentSearchProps.field.clearButtonEnabled = false;

        return componentSearchProps;
    }

    private renderIsSearch = (searchColumn: SearchBaseColumn, hastDetaildata: boolean, name: string, cell, options, root) => {
        const searchProps = this.getSearchParamsByType(searchColumn, name, options);

        let search = <IsSearchField {...searchProps}
            onOpened={() => { }}
            onChange={(q, newValue, fullObject) => {
                this.grid.editCell(cell);
                this.handleValueChanged(options, name, newValue, hastDetaildata, fullObject);
                this.grid.editCell(cell);
            }}
            value={options.model[name]}
            onClose={() => {
                this.grid.editCell(cell);
                this.renderIsSearch(searchColumn, hastDetaildata, name, cell, options, root);
            }}
            preventButtonFocus={true}
            handleIsSearchFieldFullObjectModel={() => null}
            required={searchColumn.required && searchColumn.required(options.model)}
        />

        if (searchColumn.required && searchColumn.required(options.model))
            root.render(<>
                {search}
                <span className='k-invalid-msg' data-for={options.field}></span>
            </>);
        else
            root.render(search);

        this.setState({}, () => {
            let field = this.state.Schema.fields[options.field];
            $(cell).find(":input").attr("data-required-msg", field?.Validation?.required?.message);
        });
    }

    private handleValueChanged = async (options, fieldName, newValue, hasDetailData: boolean, fullObject) => {
        const fetchingId = kendo.guid();
        options.model.set(fieldName, newValue);
        if (hasDetailData) {
            const placeHolderObj = { "__fetchingId": fetchingId, };
            options.model.set(fieldName + additonalDataSuffix, placeHolderObj);

            const result = await this.props.resolveAdditionalData(newValue, fieldName, fullObject, options);
            options.model[fieldName + additonalDataSuffix] = result;

            if (this.fetchingCallbacks[fetchingId]) {
                this.fetchingCallbacks[fetchingId](options.model, options.model[fieldName + additonalDataSuffix]);
                delete this.fetchingCallbacks[fetchingId];
            }

            this.grid.resize(true);
        }
    }

    private getSearchEditor = (searchColumn: SearchBaseColumn, hasDetailData: boolean, container, options) => {
        const node = document.createElement("div");
        $(container).append(node);
        let root = ReactDOM["createRoot"](node);

        this.renderIsSearch(searchColumn, hasDetailData, options.field, container, options, root);
    }

    private getTemplateParams = (row: any, column: DataEntryColumn): TemplateParams => {
        return {
            kendoTemplate: column.template,
            props: { ...this.props },
            row: row,
            field: column.field,
            value: row[column.field],
            functions: {
                __getDropDownText: (fieldName, keyValue) => {
                    try {
                        let column: DropDownColumn = (this.state.Columns.find(x => x.field == fieldName) as any);
                        if (column.useDataSource)
                            return column.getChoices().find(x => x.Value == keyValue)?.Text ?? keyValue;
                        else
                            return column.choices.find(x => x.Value == keyValue)?.Text ?? keyValue;
                    } catch (e) {
                        return '<div style = "color:red">Error getDropDownText: ' + e.message + '</div>';
                    }
                },
                __getFileIconClass: (fileName: string, dokuType: number = 0) => {
                    try {
                        let iconClass = "fal fa-file";
                        if (dokuType === 5) {
                            iconClass = "fal fa-external-link"
                        } else if (Helper.hasValue(fileName)) {
                            const fileNameArr = fileName.split(".");
                            const icons = fileIcons.filter(f => f.fileExtensions.indexOf(fileNameArr[fileNameArr.length - 1].toLocaleLowerCase()) > -1);
                            if (icons.length > 0) {
                                iconClass = icons[0].iconName;
                            } else {
                                iconClass = "fal fa-file-alt";
                            }
                        }
                        return iconClass;
                    } catch (e) {
                        return '<div style = "color:red">Error getFileIconClass: ' + e.message + '</div>';
                    }
                }
            }
        }
    }

    private getTemplateParamsGroupHeader = (data: any, column: DataEntryColumn) => {
        let row = EditableGridHelper.helper.getRow(data);
        let params = this.getTemplateParams(row, column);
        params.kendoTemplate = column.groupHeaderTemplate;
        if (data.value)
            params.value = data.value;
        return params;
    }

    private getColumnTemplate = (row, column, field) => {
        let templateParams = this.getTemplateParams(row, column);
        templateParams.field = field;

        if (field["__fetchingId"] && !Helper.useMobileGui()) {
            const domId = field["__fetchingId"];
            this.fetchingCallbacks[field["__fetchingId"]] = (row, field) => {
                const domElement = document.getElementById(domId);
                if (domElement) {
                    templateParams.row = row;
                    templateParams.field = field;
                    const domString = EditableGridHelper.helper.getTemplateString(templateParams);
                    const parent = domElement.parentElement;
                    domElement.remove();
                    parent.innerHTML += domString;
                }
            };
            return `<div id="${domId}" class="k-loading-image"><div style="
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;">${templateParams.value}</div><div>`
        }

        return EditableGridHelper.helper.getTemplateString(templateParams);
    }

    private getDataEntryColumnTemplate = (row, column) => {
        let templateParams = this.getTemplateParams(row, column);

        if (field["__fetchingId"] && !Helper.useMobileGui()) {
            const domId = field["__fetchingId"];
            this.fetchingCallbacks[field["__fetchingId"]] = (row, field) => {
                const domElement = document.getElementById(domId);
                if (domElement) {
                    templateParams.row = row;
                    templateParams.field = field;
                    const domString = EditableGridHelper.helper.getTemplateString(templateParams);
                    const parent = domElement.parentElement;
                    domElement.remove();
                    parent.innerHTML += domString;
                }
            };
            return `<div id="${domId}" class="k-loading-image"><div style="
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;">${templateParams.value}</div><div>`
        }

        return EditableGridHelper.helper.getTemplateString(templateParams);
    }

    private getValidationResult = (dataItem): ValidationResult => {
        if (!this.props.onRequestItemValidation) {
            return new ValidationBuilder()
                .checkReqiredFields(this.state.Columns, dataItem)
                .build();
        }
        return this.props.onRequestItemValidation(this.state.Columns, dataItem);
    }

    private getValidationTemplate = (validationResult) => {
        if (validationResult == null || validationResult.validationResultType == ValidationResultType.Info)
            return this.validationRenderer.generateValidationHtml(null);
        return this.validationRenderer.generateValidationHtml(validationResult);
    }

    private setValidationColumnTemplate = (dataItem) => {
        if (Helper.hasValue(this.props.validatable) && !this.props.validatable)
            return;

        let validationResult = this.getValidationResult(dataItem);
        var tr = $("[data-uid='" + dataItem.uid + "']", this.grid.tbody);
        const validationCell = $(tr).children('td[data-field="validatable"]');
        const errortemplate = this.getValidationTemplate(validationResult);
        validationCell.html(errortemplate);
    }

    private generateKendoColumns = (columns: BaseColumn[]): any[] => {
        if (!columns) {
            return [];
        }
        const kendoColumns = [];
        for (let column of columns) {
            const kendoColumn: kendo.ui.GridColumn = { width: column.width || "150px" };

            if (column instanceof BaseColumn) {
                kendoColumn["type"] = column.type;
                kendoColumn.title = column.title;
                if (column.format) {
                    kendoColumn.format = column.format;
                }
                kendoColumn.headerTemplate = column.headerTemplate;
                kendoColumn.groupHeaderTemplate = column.groupHeaderTemplate;
                kendoColumn.groupHeaderColumnTemplate = column.groupHeaderColumnTemplate;
                kendoColumn.media = column.media;
                kendoColumn.hidden = column.field == "mobileTemplate" ? !Helper.useMobileGui() : Helper.useMobileGui();
                kendoColumn.attributes = {
                    "data-field": column.field,
                    "data-column-type": column.type
                };
            }
            if (column instanceof DataEntryColumn) {
                const dataEntryColumn = column as DataEntryColumn;
                kendoColumn.field = dataEntryColumn.field;
                if (Helper.hasValue(dataEntryColumn.template))
                    kendoColumn.template = (row) => this.getDataEntryColumnTemplate(row, dataEntryColumn);
                if (dataEntryColumn.editable) {
                    kendoColumn.editable = (row) => dataEntryColumn.editable(row);
                }
                kendoColumn.aggregates = dataEntryColumn.aggregates;
                kendoColumn.filterable = dataEntryColumn.filterable;

                if (Helper.hasValue(column.commands)) {
                    kendoColumn.command = column.commands;
                }

                if (column.multiLine) {
                    kendoColumn.editor = EditableGridHelper.editors.getMultiLineEditor.bind(this, column, this.state.Schema);
                    kendoColumn.attributes = Object.assign(kendoColumn.attributes, { style: "white-space: nowrap; text-overflow: ellipsis; overflow: hidden;" });
                }
            }
            if (column instanceof ComponentColumn) {
            }
            if (column instanceof DropDownOnDemandColumn) {
                const dropDownOnDemand = column as DropDownOnDemandColumn;
                kendoColumn.editor = EditableGridHelper.editors.getDropDownOnDemandEditor.bind(this, dropDownOnDemand, this.state.Schema);
                kendoColumn.template = (row) => EditableGridHelper.templates.getLookupTemplateOnDemand(dropDownOnDemand, this.getTemplateParams(row, dropDownOnDemand));
                kendoColumn.groupHeaderTemplate = (e) => EditableGridHelper.groupHeaderTemplate.getDropDownOnDemandTemplate(dropDownOnDemand, this.getTemplateParamsGroupHeader(e, dropDownOnDemand));
            }
            else if (column instanceof ComboBoxColumn) {
                const comboBoxColumn = column as ComboBoxColumn;
                kendoColumn.editor = EditableGridHelper.editors.getComboBoxEditor.bind(this, comboBoxColumn, this.state.Schema);
                kendoColumn.template = (row) => EditableGridHelper.templates.getLookupTemplate(comboBoxColumn, this.getTemplateParams(row, comboBoxColumn));
                kendoColumn.groupHeaderTemplate = (e) => EditableGridHelper.groupHeaderTemplate.getDropDownTemplate(comboBoxColumn, this.getTemplateParamsGroupHeader(e, comboBoxColumn));
            }
            else if (column instanceof DropDownColumn) {
                const dropDownColumn = column as DropDownColumn;
                kendoColumn.editor = EditableGridHelper.editors.getDropDownEditor.bind(this, dropDownColumn, this.state.Schema);
                kendoColumn.template = (row) => EditableGridHelper.templates.getLookupTemplate(dropDownColumn, this.getTemplateParams(row, dropDownColumn));
                kendoColumn.groupHeaderTemplate = (e) => EditableGridHelper.groupHeaderTemplate.getDropDownTemplate(dropDownColumn, this.getTemplateParamsGroupHeader(e, dropDownColumn));
            }
            else if (column instanceof SearchBaseColumn) {
                const searchColumn = column as SearchBaseColumn;
                kendoColumn.editor = this.getSearchEditor.bind(this, column, !!column.template);
                const additionalDataFieldName = column.field + additonalDataSuffix;
                kendoColumn.template = (row) => row[additionalDataFieldName] ? this.getColumnTemplate(row, searchColumn, row[additionalDataFieldName]) : row[searchColumn.field] || '';
                kendoColumn.groupHeaderTemplate = (e) => EditableGridHelper.groupHeaderTemplate.getIsSearchTemplate(searchColumn, this.getTemplateParamsGroupHeader(e, searchColumn), additionalDataFieldName);
            }
            else if (column instanceof CheckBoxColumn) {
                const checkboxColumn = column as CheckBoxColumn;
                kendoColumn.template = (row) => EditableGridHelper.templates.getCheckboxTemplate(checkboxColumn, this.getTemplateParams(row, checkboxColumn));
            }
            else if (column instanceof TimeTextBox) {
                const timeTextBoxColumn = column as TimeTextBox;
                kendoColumn.editor = EditableGridHelper.editors.getTimeTextBoxEditor.bind(this);
                kendoColumn.template = (row) => EditableGridHelper.templates.getTimeTextBoxTemplate(timeTextBoxColumn, this.getTemplateParams(row, timeTextBoxColumn));
                kendoColumn.groupHeaderColumnTemplate = (data) => EditableGridHelper.groupHeaderColumnTemplate.getTimeTextBoxTemplate(timeTextBoxColumn, data, { ...this.props });
            }
            else if (column instanceof MultiColumn) {
                const multiColumn = column as MultiColumn;
                let subColumns = this.generateKendoColumns(multiColumn.columns);
                kendoColumn.title = multiColumn.title;
                kendoColumn.field = multiColumn.field;
                kendoColumn.media = column.media;
                kendoColumn.hidden = column.field == "mobileTemplate" ? !Helper.useMobileGui() : Helper.useMobileGui();
                kendoColumn.columns = subColumns;
            }
            kendoColumns.push(kendoColumn);
        }

        return kendoColumns;
    }

    private getDefaultMobileTemplate = (kendoColumns: kendo.ui.GridColumn[], row) => {
        const getMobileTemplateRecursiv = function (kendoColumns: kendo.ui.GridColumn[], row) {
            let template = "";
            for (let kendoColumn of kendoColumns) {
                if (kendoColumn.columns) {
                    template += getMobileTemplateRecursiv(kendoColumn.columns, row);
                } else {
                    if (!Helper.hasValue(kendoColumn.title))
                        continue;
                    let columnTemplate = '';
                    try {
                        columnTemplate = EditableGridHelper.helper.getTemplate(kendoColumn, row) ?? '';
                    }
                    catch (e) {
                        columnTemplate = '<div style = "color:red">Error im template:' + e.message + '</div>';
                    }

                    template += `
                        <div class='list-item__title list-item__title--small'>${kendoColumn.title}:</div>
                        <div class='list-item__row'>${columnTemplate}</div>
                    `;
                }
            }
            return template;
        }

        return "<div class='list-item list-item--grid'>" + getMobileTemplateRecursiv(kendoColumns, row) + "</div>";
    }

    private dataSource: kendo.data.DataSource;

    private insertDataItem = (item: any) => {
        return this.dataSource.add(item);
    }

    private insertDataItems = (dataItems: any) => {
        let insertedItems = [];
        for (let dataItem of dataItems)
            insertedItems.push(this.insertDataItem(dataItem ?? {}));
        return insertedItems;
    }

    private delete_ = async (items: any[], withConfirmation: boolean) => {
        let isOk = true;
        if (withConfirmation)
            isOk = await DialogHelper.confirm(Config.translations.deleteMessage);

        if (isOk) {
            for (let i of items) {
                this.grid.dataSource.remove(i);
            }
            this.grid.clearSelection();
        }
    }

    private deleteSelected = async (withConfirmation: boolean) => {
        if (this.hasSelected()) {
            const rows = this.grid?.select();
            const items: any = rows.map((index, row) => { return this.grid.dataItem(row) });
            await this.delete_(items, withConfirmation ?? false);
        } else {
            DialogHelper.alert(Helper.getTranslation("WebSMS.WeeklyReport.PleaseSelectEntry"));
        }
    }
    
    public getColumns = () => {
        return this.kendoColumns;
    }

    getSelectedItemsArray = (): any[] => {
        if (!this.grid)
            return [];
        const rows = this.grid.select();
        const items = rows.map((index, row) => { return this.grid.dataItem(row) });
        return items.toArray();
    }

    private hasSingleSelection = (): boolean => {
        return this.grid?.select().length == 1;
    }

    public hasSelected = (): boolean => {
        const rows = this.grid?.select();
        return rows && rows.length > 0;
    }

    private handleGridContainerResized = () => {
        this.grid?.resize();
    }

    componentDidUpdate(prevProps: EditableGridProps) {
        if ((prevProps.data || []) !== (this.props.data || [])) {
            this.dataSource?.read();
            this.setState({});
        }
        if (prevProps.filters !== this.props.filters) {
            this.dataSource?.filter(this.props.filters);
            this.dataSource?.view();
        }
        if (this.props.editable != prevProps.editable) {
            const op = this.grid.options;
            op.editable = this.props.editable;
            this.grid?.setOptions(op);
        }
    }

    private openEditInGridRowByItem = (dataItem: any, withGeneralFields: boolean) => {
        this.openEditRow(dataItem, this.state.Columns, withGeneralFields, this.props.helpEditUrl);
    }

    openEditInGridRow = (withGeneralFields: boolean) => {
        let dataItem = this.getSelectedItems()[0];
        this.openEditRow(dataItem, this.state.Columns, withGeneralFields, this.props.helpEditUrl)
    }

    private openEditRow = (dataItem, columns, withGeneralFields: boolean, helpUrl: string = "") => {
        let dataItemBefore = JSON.parse(JSON.stringify(dataItem));
        this.editableSheet = General.Sheet.Manager.createSheet();
        this.editableSheet.setTitle(Helper.getTranslation("Standard.Bearbeiten"));
        this.editableSheet.show(() => {
            this.grid.closeCell();

            let props: EditInGridRowProps = {
                dataSources: null,
                entity: {
                    IsNew: false, IsWatchable: false, IsWatched: false, RecordRight: { CanRead: true, CanWrite: this.props.editable },
                    dataItem: dataItem,
                    gridOptions: this.grid.options
                },
                isWindow: false,
                metadata: getEditInGridMetaData(columns, dataItem, withGeneralFields, helpUrl) as Metadata,
                context: {
                    contextType: "",
                },
                id: dataItem[this.state.Schema.Id] ?? kendo.guid()
            }

            this.editableSheet.mountContent(EditInGridRow, props);
        });

        this.editableSheet.bind(SheetEventType.Custom, (cus: { command: string, count: number }) => {
            if (cus.command === "updateDocumentsCount") {
                const docColumns = columns.filter(f => f instanceof (ComponentColumn) && f.moduleName == "app/documents/documents")
                docColumns.forEach(e => {
                    const updateCountField = e.props({})["updateCount"];
                    if (Helper.hasValue(updateCountField)) {
                        dataItem["preventOnDataChanged"] = true;
                        dataItem.set(updateCountField, cus.count);
                    }
                });
            }
        });

        this.editableSheet.bind(SheetEventType.SavedSuccessfully, (data) => {
            if (Helper.hasValue(this.props.onSaveSuccesfully)) {
                this.props.onSaveSuccesfully(data);
            }
        });

        this.editableSheet.bind(SheetEventType.Close, (data) => {
            if (this.editableSheet.hasUnsavedChanges()) {
                for (let propName in dataItemBefore) {
                    let currentValue = dataItem[propName];
                    let beforeValue = dataItemBefore[propName];
                    if (currentValue != beforeValue)
                        dataItem.set(propName, dataItemBefore[propName]);
                }
            }
            this.editableSheet = null;
        });
    }

    private getGridOptions = (kendoColumns) => {
        function pageable() {
            let pageable: boolean | kendo.ui.GridPageable = that.props.pageable ?? true;
            if (pageable && Config.settings.isMobileApp)
                pageable = {
                    position: "top",
                    info: false,
                    numeric: false
                }
            return pageable;
        }

        let mobileTemplateColumn = kendoColumns.find(x => typeof x.field === 'string' && x.field?.toLowerCase() == "mobiletemplate");
        if (!mobileTemplateColumn && Helper.useMobileGui()) {
            kendoColumns.unshift({
                field: "mobileTemplate",
                width: "100%",
                template: (row) => this.getDefaultMobileTemplate(kendoColumns.filter(x => x.field != "selectable" && x.field != "validatable" && (typeof x.field !== 'string' || x.field?.toLowerCase() != "mobiletemplate")), row),
                hidden: !Helper.useMobileGui(),
                editable: (_) => false,
                attributes: {
                    "data-field": "mobileTemplate"
                }
            });
        } else if (mobileTemplateColumn) {
            mobileTemplateColumn.editable = () => false;
            mobileTemplateColumn.width = "100%";
        }

        if (Helper.useMobileGui() && this.getButtons(this.props.buttons).find(x => x.useContextMenu)) {
            kendoColumns.push({
                field: "mobileContextMenu",
                width: "30px",
                hidden: !Helper.useMobileGui(),
                editable: (_) => false,
                attributes: {
                    "data-field": "mobileContextMenu"
                },
                template: (row) => `
                    <div class='mobile-context-menu'>
                        <i class='fal fa-ellipsis-vertical fa-2x'></i>
                    </div>`
            })
        }

        if (!Helper.hasValue(this.props.showSelectableCheckBox) || this.props.showSelectableCheckBox) {
            kendoColumns.unshift({
                field: "selectable",
                selectable: true,
                filterable: false,
                sortable: false,
                width: "60px",
                hidden: Helper.useMobileGui(),
                headerTemplate: this.props.selectableCheckBoxHeaderTemplate ?
                    `
                        <span class="k-cell-inner">
                            <span class="k-link" style='column-gap: 0.5rem;'>
                                <input tabindex="-1" class="k-select-checkbox k-checkbox k-checkbox-md k-rounded-md k-valid" data-role="checkbox" aria-label="Select all rows" aria-checked="false" type="checkbox" />
                                <span class='k-column-title'>${this.props.selectableCheckBoxHeaderTemplate}</span>
                            </span>
                        </span>
                    `
                    : null,
                attributes: {
                    "data-field": "selectable"
                }
            });
        }

        if (kendoColumns && kendoColumns.length > 0 && (!Helper.hasValue(this.props.validatable) || this.props.validatable)) {
            kendoColumns.unshift({
                field: "validatable",
                title: "",
                width: "60px",
                template: (row) => {
                    let validationResult = this.getValidationResult(row);
                    return this.getValidationTemplate(validationResult);
                },
                headerTemplate: "",
                editable: () => false,
                filterable: false,
                hidden: Helper.useMobileGui(),
                attributes: {
                    "data-field": "validatable"
                }
            });
        }

        if (!Helper.useMobileGui())
            kendoColumns = this.getLocalStorageAndOrderColumns(kendoColumns);

        this.dataSource = this.createDataSource(this.state.Schema);
        let that = this;

        let noRecordsText = Helper.getTranslation("WebSMS.Grid.NoRecords");
        try {
            noRecordsText = kendo.ui.Grid.prototype.options.messages.noRecords;
        } catch {
            //
        }
        
        const gridOptions: kendo.ui.GridOptions = {
            dataSource: this.dataSource,
            persistSelection: true,
            navigatable: true,
            groupable: !Helper.hasValue(this.props.groupable) || this.props.groupable,
            resizable: true,
            reorderable: true,
            columnResize: (e) => {
                if (Helper.useMobileGui())
                    return;
                this.setLocalStorage();
            },
            columnReorder: (e) => {
                if (Helper.useMobileGui())
                    return;
                this.columnReorder();
            },
            pageable: pageable(),
            sortable: this.props.sortable,
            selectable: this.props.selectable,
            filterable: this.props.filterable,
            scrollable: this.props.scrollable,
            columns: kendoColumns,
            editable: this.props.editable || false,
            noRecords: {
                template: `<div style='width: 100%'>${noRecordsText}</>`
            },
            navigate: (e: kendo.ui.GridNavigateEvent) => {
                if (this.props.onNavigate)
                    this.props.onNavigate(e);
            },
            edit: (e) => {
                if (this.props.rowReadOnly && this.props.rowReadOnly(e.model)) {
                    e.sender.closeCell();
                }

                if (Helper.hasValue(this.props.onEdit))
                    this.props.onEdit(e);
            },
            cellClose: (e) => {
                if (this.props.validateAll) {
                    let data = this.getDataSource().data();
                    data.forEach(item => {
                        this.setValidationColumnTemplate(item);
                    });
                } else {
                    this.setValidationColumnTemplate(e.model);
                }
                if (Helper.hasValue(this.props.onEditEnd)) {
                    this.props.onEditEnd(e);
                }
            },
            change: (e) => {

                that.checkSelection(e.sender);
                if (this.props.onSelectionChange)
                    this.props.onSelectionChange(e);

                this.setState({});
            },
            dataBound: (e) => {
                let grid = e.sender;
                e.sender.tbody.find("tr.k-master-row").on("click", function (e) {
                    that.onRowClick(e, this);
                });

                if (Helper.useMobileGui()) {
                    let isScrolling = false;
                    e.sender.tbody.find("tr.k-master-row")
                        .on("touchstart", function () {
                            isScrolling = false;
                        })
                        .on("touchmove", function () {
                            isScrolling = true;
                        }).on("touchend", function (e) {
                            if (!isScrolling) {
                                that.selectRow(this);
                            } else {
                                isScrolling = false;
                            }
                        });

                    e.sender.tbody.find(".mobile-context-menu")
                        .on("click", function (e) {
                            that.onContextMenuClick(e);
                        }).on("touchstart", function (e) {
                            e.stopPropagation();
                        });
                }

                e.sender.tbody.find("[data-column-type='component']").on("click", function (e) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    let columnField = $(e.target).closest("td").attr("data-field");
                    let row = $(e.target).closest("tr");
                    let dataItem = that.grid.dataItem(row);
                    that.onComponentCick(columnField, dataItem);
                });

                e.sender.tbody.find("tr").each(function () {
                    that.checkSelection(e.sender);
                });

                if (this.props.onDataBound)
                    this.props.onDataBound(e);
            },
            filter: (e) => {
                if (this.props.onFilter)
                    this.props.onFilter(e);
            }
        }
        if (this.props.onFieldChanged) {
            gridOptions.save = this.props.onFieldChanged;
        }

        return gridOptions;
    }

    private onRowClick(e, row) {
        if (Helper.useMobileGui()) {
            this.selectRow(row);
            InnosoftMobileApp.isMobileDevice().then(isMobileDevice => {
                if ((isMobileDevice || !(this.props.gridDetails?.useFeature ?? false))) {
                    this.openEditInGridRow(true);
                }
            })
        }

        if (Helper.hasValue(this.props.onRowClicked)) {
            let dataItem = this.grid.dataItem(row);
            this.props.onRowClicked(e, dataItem);
        }
    }

    private selectRow(row) {
        this.grid.clearSelection();
        this.grid.select(row);
        this.grid.trigger("change");
    }

    private onContextMenuClick(e) {
        e.stopPropagation();
        this.selectRow(e.target.closest("tr"));
        this.setState({}, () => this.contextMenu.openContextMenu(e.target));
    }

    private onComponentCick = (columnField: string, dataItem: any) => {
        let baseColumn = this.state.Columns.find(x => x.field == columnField);
        if (baseColumn instanceof ComponentColumn) {
            if (dataItem.IsNew)
                NotificationHelper.showWarningMessage(Helper.getTranslation("WebSMS.Messages.SaveDataFirst"));
            else
                this.openEditRow(dataItem, [baseColumn], false, baseColumn.helpUrl);
        }
    }

    private checkSelection = (grid: kendo.ui.Grid) => {
        if (this.props.rowReadOnly) {
            let that = this;
            let items = grid.select();
            items.each(function (i, row) {
                let dataItem = grid.dataItem(row);
                if (that.props.rowReadOnly(dataItem)) {
                    let checkbox = $(row).find("input[type=checkbox]");
                    $(checkbox).prop("checked", false);
                    $(row).removeClass("k-state-selected");
                }
            });
        }
    }

    componentDidMount() {
        if (this.props.gridOptions)
            new IngridHelper(this).initGridSettings(this.props.gridOptions, this.props.schema).then(result => {
                if (this.props.onLayoutReady)
                    this.props.onLayoutReady(result.Columns, result.Schema);
                this.setState({
                    Columns: result.Columns, Schema: result.Schema
                }, () => {
                    this.init();
                });
            });
        else
            this.init();
    }

    private init = () => {
        this.createGrid();
    }

    private createGrid = () => {
        this.kendoColumns = this.generateKendoColumns(this.state.Columns);
        let gridOptions = this.getGridOptions(this.kendoColumns);

        this.grid = $(this.gridRef).kendoGrid(gridOptions).data("kendoGrid");

        $(this.gridRef).kendoTooltip({
            filter: "th",
            position: "bottom",
            content: function (e: any) {
                const text = e.target.text();
                if (text == null || text == "")
                    throw 'normal exception to prevent empty tooltip';
                return text;
            }
        }).data("kendoTooltip");
    }

    private scrollToSelectedElement = () => {
        var scrollContentOffset = this.grid.element.find("tbody").offset().top;
        var selectContentOffset = this.grid.select().offset().top;
        var distance = selectContentOffset - scrollContentOffset;

        this.grid.element.find(".k-grid-content").animate({
            scrollTop: distance
        }, 400);
    }

    private columnReorder() {
        //Im Event sind die Spalten noch in der alten Sortierung
        //Nach dem Timeout sollten die Spalten dann in der richtigen Sortierung vorliegen
        let that = this;
        setTimeout(function () {
            that.setLocalStorage();
        }, 5);
    }

    setLocalStorage() {
        function getColumnInfo(gridColumn: kendo.ui.GridColumn) {
            let columInfo: ColumnInfo = {
                name: gridColumn.field ?? "dummy_field",
                width: gridColumn.width,
                columns: []
            }
            if (Helper.hasValue(gridColumn.columns) && gridColumn.columns.length > 0) {
                for (let subColumn of gridColumn.columns) {
                    let subColumnInfo = getColumnInfo(subColumn);
                    columInfo.columns.push(subColumnInfo);
                }
            }
            return columInfo;
        }

        let localStorageColumns: ColumnInfo[] = this.grid.getOptions()
            .columns
            .filter(x => Helper.hasValue(x))
            .map(item => getColumnInfo(item));
        Helper.saveDataToStorage(ComponentHelper.getKendoGridStorageKey(this.props.id), localStorageColumns, false);
    }


    getLocalStorageAndOrderColumns(kendoColumns) {
        let localStorageColumns: Array<ColumnInfo> = Helper.loadDataFromStorage(ComponentHelper.getKendoGridStorageKey(this.props.id));
        return ComponentHelper.orderColumns(this.props.id, localStorageColumns, kendoColumns, false);
    }

    private mapDataToIndFields(entity: Entity) {
        for (const name in entity) {
            if (name.includes("Ind_")) {
                let indName = name.replace("Ind_", "");
                entity.IndividualFields[indName] = entity[name];
            }
        }
    }

    private mapIndFieldsToData(data: Entity[]) {
        const regexIndividualFields = "^(IndividualFields)[a-zA-Z]*_";

        function mapFields(row, obj, name = '') {
            for (let prop in obj) {
                let stackName = Helper.isNullOrEmpty(name) ? prop : name + '_' + prop;
                if (typeof (obj[prop]) === 'object' && Helper.hasValue(obj[prop]) && prop != 'IndividualFields') {
                    mapFields(row, obj[prop], stackName);
                }
                else if (stackName.match(regexIndividualFields)) {
                    row[stackName] = obj[prop];
                }
            }
        }

        for (const row of data) {
            for (let indName in row.IndividualFields) {
                row["Ind_" + indName] = row.IndividualFields[indName];
            }
            mapFields(row, row);
        }
    }

    private setDefaultValues(columns: BaseColumn[], dataItem) {
        for (let column of columns) {
            if (column instanceof DataEntryColumn) {
                if (!Helper.hasValue(column.defaultValue))
                    return;
                let defaultValue = column.defaultValue(dataItem);
                if (defaultValue != null && Helper.isNullOrEmpty(dataItem[column.field])) {
                    dataItem[column.field] = defaultValue;
                }
            } else if (column instanceof MultiColumn) {
                this.setDefaultValues(column.columns, dataItem);
            }
        }
    }

    private handleButtonClick = (id: string) => {
        if (this.props.onButtonClick) {
            const actions: InGridActions = {
                delete: async (items, withConfirmation) => await this.delete_(items, withConfirmation ?? false),
                deleteSelected: async (withConfirmation) => await this.deleteSelected(withConfirmation ?? false),
                edit: (item) => this.openEditInGridRowByItem(item, true),
                editSelected: () => this.openEditInGridRow(true),
                add: (item) => {
                    let model = this.insertDataItem(item ?? {});

                    if (Helper.useMobileGui()) {
                        let row = this.grid.table.find('tr[data-uid="' + model.uid + '"]');
                        this.selectRow(row);
                        this.scrollToSelectedElement();
                        InnosoftMobileApp.isMobileDevice().then(isMobileDevice => {
                            if (isMobileDevice)
                                this.openEditInGridRow(true);
                        })
                    }

                    return model;
                },
                addBulk: (items) => this.insertDataItems(items),
                clearSelection: () => this.grid.clearSelection()
            }

            const selectedItems: any = this.getSelectedItemsArray();
            const dataSource = this.dataSource;
            this.props.onButtonClick({
                id: id,
                actions: actions,
                selectedItems: selectedItems,
                dataSource: dataSource
            });
        }
    }

    private getButtons = (buttons: InGridToolBarButton[]) => {
        if (Array.isArray(buttons)) {
            for (let button of buttons) {
                switch (button.type) {
                    case ToolBarInGridButtonType.NEW:
                        button.caption = button.caption ?? Config.translations.newLabel;
                        button.enabled = button.enabled ?? (() => this.props.editable);
                        button.iconClass = button.iconClass ?? ToolBarStandardIconClass.NEW;
                        button.useContextMenu = button.useContextMenu ?? false;
                        break;
                    case ToolBarInGridButtonType.EDIT:
                        button.caption = button.caption ?? Config.translations.editLabel;
                        button.enabled = button.enabled ?? (() => this.props.editable && this.hasSingleSelection());
                        button.iconClass = button.iconClass ?? ToolBarStandardIconClass.EDIT;
                        button.useContextMenu = button.useContextMenu ?? true;
                        break;
                    case ToolBarInGridButtonType.DELETE:
                        button.caption = button.caption ?? Config.translations.deleteLabel;
                        button.enabled = button.enabled ?? (() => this.props.editable && this.hasSelected());
                        button.iconClass = button.iconClass ?? ToolBarStandardIconClass.DELETE;
                        button.useContextMenu = button.useContextMenu ?? true;
                        break;
                    default:
                        button.caption = button.caption ?? "";
                        button.enabled = button.enabled ?? (() => this.props.editable);
                        button.iconClass = button.iconClass ?? "";
                        button.useContextMenu = button.useContextMenu ?? false;
                        break;
                }
            }
            return buttons;
        }
        return [];
    }

    private openContextMenu = (params: ToolBarContextMenuOpenParams) => {
        if (params.e.event && params.e.event.type == "contextmenu")
            params.e.preventDefault(); //this.selectRow(params.e.target.closest("tr"));

        let enabledParams = {
            hasSingleSelection: this.hasSingleSelection(),
            hasSelection: this.hasSelected(),
            selectedItems: this.getSelectedItemsArray(),
            dataSource: this.dataSource
        };

        for (let btn of params.buttons) {
            let enabled = btn.enabled(enabledParams) && !(this.props.rowReadOnly && this.props.rowReadOnly(this.getSelectedItems()[0]));
            params.enable(btn, enabled);
        }
    }

    public forceUpdate = () => {
        this.grid.dataSource.read();
    }

    public updateDataDirty = () => {
        this.dataSource = this.createDataSource(this.state.Schema);
        this.grid.setDataSource(this.dataSource)
    }

    render() {
        this.mapIndFieldsToData(this.props.data);

        const btnToolBar: Array<ToolBarCustomButton> = [];
        let buttons = this.getButtons(this.props.buttons);
        let enableParams: InGridButtonEnabledParams = {
            hasSingleSelection: this.hasSingleSelection(),
            hasSelection: this.hasSelected(),
            selectedItems: this.getSelectedItemsArray(),
            dataSource: this.dataSource
        }
        for (let btn of buttons) {
            btnToolBar.push({
                caption: btn.caption,
                enabled: btn.enabled(enableParams),
                iconClass: btn.iconClass,
                id: btn.id,
                width: btn.width,
                useContextMenu: btn.useContextMenu
            });
        }

        return <>
            <ResizeDetector handleWidth handleHeight onResize={this.handleGridContainerResized} />
            <div className="flex-container editable-grid-container">
                <ToolBar customButtons={btnToolBar}
                    handleCustomButtonClick={this.handleButtonClick}
                    useFloatingButtons={Helper.useMobileGui() && (this.props.useFloatingButtons ?? false)}
                    contextMenu={{
                        use: Helper.useMobileGui(),
                        target: this.gridId,
                        filter: "tbody>tr",
                        visible: Helper.useMobileGui(),
                        open: this.openContextMenu,
                        ref: (ref) => this.contextMenu = ref
                    }}
                />
                <div className="flex-container flex-container--row flex-container__item">
                    <div className="flex-container flex-container__item" id={this.gridId} ref={(r) => this.gridRef = r}></div>
                    <GridDetails
                        columns={this.grid?.options?.columns}
                        dataItem={this.hasSingleSelection() ? this.getSelectedItems()[0] : null}
                        id={this.hasSingleSelection() ? this.getSelectedItems()[0][this.state.Schema.Id] : null}
                        visible={(Helper.useMobileGui() && (this.props.gridDetails?.useFeature ?? false))}
                        editable={this.props.editable}
                        onTemplateClick={this.onComponentCick}
                        isSheet={false}
                    />
                </div>
            </div>
        </>;
    }
}

class KendoValidationMessageRenderer {
    private errorTemplate = kendo.template('<div><span style="border-color:red;background-color:red;color:white;font-size:28px;border-radius:50px" class="k-tooltip-icon k-icon k-i-warning" title="#=message#"></span></div>');
    private warningTemplate = kendo.template('<div><span style="border-color:red;background-color:yellow;color:white;font-size:28px;border-radius:50px" class="k-tooltip-icon k-icon k-i-warning" title="#=message#"></span></div>');
    private infoTemplate = kendo.template('<div><span style="border-color:red;background-color:lightgreen;color:white;font-size:28px;border-radius:50px" class="k-tooltip-icon k-icon k-i-warning" title="#=message#"></span></div>');

    private getTemplate = (validationResultType: ValidationResultType): any => {
        switch (validationResultType) {
            case ValidationResultType.Info:
                return this.infoTemplate;
            case ValidationResultType.Error:
                return this.errorTemplate;
            case ValidationResultType.Warning:
                return this.warningTemplate;
        }
    }

    generateValidationHtml = (validationresult: ValidationResult) => {
        if (!validationresult) {
            return '';
        }

        try {
            let template = this.getTemplate(validationresult.validationResultType);
            var result = template({ message: validationresult.validationMessage });
        }
        catch (e) {
            return '<div style = "color:red">Error im template:' + e.message + '</div>';
        }
        return result
    }
}

export class Schema {
    Id: string;
    public fields: FieldsType = {};
    public group?: Group;
    public aggregate?: Aggregate[];
}

interface Group {
    field: string;
    dir: string;
    aggregates?: Aggregate[];
}

interface Aggregate {
    field: string;
    aggregate: string
}

interface FieldsType {
    [key: string]: field;
}

export class field {
    public Type: string;
    public editable: boolean;
    public Validation: any;
    constructor(type: string, editable: boolean = true, validation: any = null) {
        this.Type = type;
        this.editable = editable;
        this.Validation = validation;
    };
}

export class BaseColumn {
    public type?: string;
    public width: string;
    public title;
    public format?;
    public groupHeaderTemplate?;
    public groupHeaderColumnTemplate?;
    public headerTemplate?;
    public defaultValue?: (dataItem) => string;
    public required?: (dataItem) => boolean;
    public commands?: kendo.ui.GridColumnCommandItem[];
    public field?: string;
    public groupFooterTemplate?: Function;
    public media?: string;
}

export class GroupingColumn extends BaseColumn {
    public childColumns: BaseColumn[]
}

export class DataEntryColumn extends BaseColumn {
    public editable?: (value: any) => boolean;
    public min?: any;
    public max?: any;
    public template?: string;
    public aggregates?: string[];
    public multiLine?: boolean;
    public multiLines?: number;
    public filterable?: boolean | kendo.ui.GridColumnFilterable;
}

export class DropDownColumn extends DataEntryColumn {
    public useDataSource: boolean;
    public choices: { Value: any, Text: any }[];
    public getChoices: () => { Value: any, Text: any }[];
    public listWidth?: number;
    public itemTemplate?: string;
    public valueTemplate: string;
    public filter?: string;
    public optionLabel?: string;
    public refreshChoices?: (dataItem) => { Value: any, Text: any }[];
    public fallbackTextField?: string;
}

export class DropDownOnDemandColumn extends DataEntryColumn {
    public lookUpField: string;
    public url: string;
    public evalUrl: string;
    public getUrl: (dataItem) => string;
    public getChoices: (dataItem) => Promise<{ Value: any, Text: any }[]>;
    public listWidth?: number;
    public itemTemplate?: string;
    public valueTemplate: string;
    public optionLabel: string;
}

export class ComboBoxColumn extends DropDownColumn {
}

export class CheckBoxColumn extends DataEntryColumn {
}

export class TimeTextBox extends DataEntryColumn {
}

export class NumericColumn extends DataEntryColumn {
}

export class DatePickerColumn extends DataEntryColumn {
}

export class ComponentColumn extends DataEntryColumn {
    onClick: (e, dataItem) => void;
    visible: (row) => boolean;
    iconName: string;
    moduleName: string;
    helpUrl: string;
    props: (dataItem) => any;
}

export class MultiColumn extends DataEntryColumn {
    columns: BaseColumn[];
}

export class SearchBaseColumn extends DataEntryColumn {
    public searchWindowUrl: string;
    public dataTextField: string;
    public dataValueField: string;
    public searchValueField: string;
    public searchTextField: string;
    public autocompleteEnabled: boolean;
    public autocompleteDataTextField: string;
    public autocompleteUrl: string;
    public autocompleteTemplate: string;
    public readOnlyTextField: boolean;
    public fullObjectKey: string;
    public deleteButtonEnabled: boolean;
}

export class MachineSearchColumn extends SearchBaseColumn {

    constructor() {
        super();
        this.searchWindowUrl = "DynamicSearch/Window/Machine";
        this.dataTextField = "MaschNr";
        this.dataValueField = "MaschNr";
        this.searchValueField = "MaschNr";
        this.searchTextField = "MaschNr";
        this.autocompleteEnabled = true;
        this.autocompleteDataTextField = "MaschNr";
        this.autocompleteUrl = "MachineLookup/Find";
        this.autocompleteTemplate = "<table cellspacing='0' style='table-layout:fixed; width:650px'><colgroup><col style='width: 100px' /><col style='width: 250px' /><col style='width: 300px' /></colgroup><tr style='background-color:# if(data.Status == 1) { #\\#cccccc# } else { #inherit# } #'><td style='vertical-align:top; word-wrap: break-word'>${data.MaschNr}</td># if(data.MaschBezeichnung != null) { #<td style='vertical-align:top'>${data.MaschBezeichnung}</td>#}else{#<td style='vertical-align:top'></td>#}#<td style='vertical-align:top'>${data.AdrName}</td></tr></table>";
    }
}

export class AddressSearchColumn extends SearchBaseColumn {

    constructor() {
        super();
        this.searchWindowUrl = "DynamicSearch/Window/Address";
        this.dataTextField = "AdrNr";
        this.dataValueField = "AdrNr";
        this.searchValueField = "AdrNr";
        this.searchTextField = "AdrNr";
        this.autocompleteEnabled = true;
        this.autocompleteDataTextField = "AdrNr";
        this.autocompleteUrl = "AddressLookup/Find";
        this.autocompleteTemplate = "<table cellspacing='0' style='table-layout:fixed; width:650px'><colgroup><col style='width: 100px' /><col style='width: 250px' /><col style='width: 300px' /></colgroup><tr style='background-color:# if(data.Status == 1) { #\\#cccccc# } else { #inherit# } #'><td style='vertical-align:top; word-wrap: break-word;'>${data.AdrNr}</td><td style='vertical-align:top'>${data.AdrName}</td><td style='vertical-align:top'>${data.Plz}  ${data.Ort}</td></tr></table>";
    }
}

export class ResourceSearchColumn extends SearchBaseColumn {

    constructor() {
        super();
        this.searchWindowUrl = "DynamicSearch/Window/Resource";
        this.autocompleteEnabled = true;
        this.autocompleteDataTextField = "Ressnr";
        this.autocompleteUrl = "ResourceLookup/FindWithId";
        this.autocompleteTemplate = "<table cellspacing='0' style='table-layout:fixed; width:250px'><colgroup><col style='width: 80px' /><col style='width: 100px' /><col style='width: 100px' /></colgroup><tr style='background-color:# if(data.StatusAsInt == 1) { #\\#cccccc# } else { #inherit# } #'><td style='vertical-align:top; word-wrap: break-word;'>${data.Ressnr}</td><td style='vertical-align:top'>${data.Name}</td><td style='vertical-align:top'>${data.Vorname}</td></tr></table>";
    }
}

export class TicketSearchColumn extends SearchBaseColumn {

    constructor() {
        super();
        this.searchWindowUrl = "DynamicSearch/Window/Procedure";
        this.autocompleteEnabled = false;
    }
}

export class ProjectSearchColumn extends SearchBaseColumn {

    constructor() {
        super();
        this.searchWindowUrl = "DynamicSearch/Window/Project";
        this.autocompleteEnabled = false;
    }
}

export class TemplateColumn extends BaseColumn {
    public template: string;
}

export class ContactSearchColumn extends SearchBaseColumn {

    constructor() {
        super();
        this.searchWindowUrl = "DynamicSearch/Window/Contact";
        this.autocompleteEnabled = true;
        this.autocompleteDataTextField = "Pos";
        this.autocompleteUrl = "ContactLookup/Find";
        this.autocompleteTemplate = "<table cellspacing='0' style='table-layout:fixed; width:650px'><colgroup><col style='width: 100px'/><col style='width: 275px'/><col style='width:275px'/></colgroup><tr><td style='vertical-align:top; word-wrap: break-word;'>${data.AdrNr}</td><td style='vertical-align:top'>${data.Ansprechpartner}</td><td style='vertical-align:top'>${data.Vorname}</td></tr></table>";
    }
}

export enum ValidationResultType {
    Info = 0,
    Warning = 1,
    Error = 2
}

export interface ValidationResult {
    validationResultType: ValidationResultType;
    validationMessage: string;
}

export enum ComponentType {
    EditField = 0,
    Numeric = 3,
    CheckBox = 4,
    ComboBox = 6,
    MultilangComboBox = 7,
    ComboBoxOnDemand = 9,
    DatePicker = 5,
    TimeTextBox = 10,
    MultiColumn = 11,
    IsSearch = 90,
    AddressSearch = 100,
    MachineSearch = 110,
    ProjectSearch = 120,
    ResourceSearch = 130,
    ContactSearch = 140,
    TicketSearch = 150
}

export class InfoItemRefresher extends React.Component<{ numberItems: number }, InfoItemProps> {

    constructor(props) {
        super(props);
        this.state = { numberItems: props.numberItems }
    }

    updateNumberItems = (numberItems: number) => {
        this.setState({ numberItems: numberItems })
    }

    render() {
        return <IndividualNDataInlineInfoItem numberItems={this.state.numberItems} />
    }
}

interface InfoItemProps { numberItems?: number }

const IndividualNDataInlineInfoItem = (props: InfoItemProps) => {
    return props.numberItems > 0 ? <span style={{
        borderColor: "black",
        borderWidth: "1px",
        borderStyle: "solid",
        backgroundColor: "lightblue",
        color: "black",
        fontSize: "11px",
        minWidth: "25px",
        paddingLeft: "2px",
        paddingRight: "2px",
        fontWeight: "bold"
    }} >{props.numberItems}</span> : null;
}

export interface InGridToolBarButton {
    type: any;
    id: string;
    caption?: string;
    iconClass?: string;
    enabled?: (params: InGridButtonEnabledParams) => boolean;
    useContextMenu?: boolean;
    width?: string;
}

export interface InGridButtonClickParams {
    id: string;
    actions: InGridActions;
    selectedItems: any[];
    dataSource: kendo.data.DataSource;
}

export interface InGridActions {
    delete: (items: any[], withConfirmation?: boolean) => void;
    deleteSelected: (withConfirmation?: boolean) => void;
    edit: (item: any) => void;
    editSelected: () => void;
    add: (item?: any) => any;
    addBulk: (items: any[]) => any[];
    clearSelection: () => void;
}

export interface TemplateParams {
    kendoTemplate: string;
    row: any;
    field: any;
    value: any;
    props: any;
    functions: {
        //Alle Funktionen mit "__" voranstellen damit es keine Nameskonflikte gibt
        __getDropDownText: (fieldName: string, keyValue: any) => any;
        __getFileIconClass: (fileName: string, dokuType: number) => string;
    }
}

export interface GridData {
    Data: any[];
    Deleted: any[];
}